import type { ReactNode } from 'react'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import type { SxProps } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'

import { getLogoElevation } from './logoShadow'

// Figma: the logo overhangs the white card's top edge by 25px and the slot pads
// 16px above that overhang (so `pt = overhang + 16`).
const LOGO_OVERHANG = 25

interface AccountHeroCardProps {
  /** Central account/institution logo, rendered overhanging the card's top edge. */
  logo: ReactNode
  /** Primary line, e.g. "Epic Bank Checking, *1234". */
  name: ReactNode
  /** Optional secondary line beneath the name, e.g. "Checking Account". */
  subtitle?: ReactNode
  /** Status element beneath the text (e.g. a `<StatusPill>`). */
  status?: ReactNode
  /** Decorative backdrop behind the card (e.g. a `<Confetti>` burst). */
  backdrop?: ReactNode
  /** Slot surface color token. Default `primary.lighter` (Figma's tinted slot,
   * theme-aware: light-blue in light mode, navy in dark). */
  bgcolor?: string
  /**
   * Optional decorative wash layered over `bgcolor` (e.g. a celebration
   * gradient). Figma's gradient is ~20% opacity over a white base, so this is a
   * `backgroundImage` on top of `bgcolor`, not a replacement — pass
   * `bgcolor="background.paper"` with it for the complete state.
   */
  backgroundImage?: string
  sx?: SxProps
}

/**
 * A centered account hero: an overhanging institution logo above a white card
 * with a name, optional subtitle, and a status slot, over a tinted (or
 * gradient + confetti) slot.
 *
 * Figma: Set Up Direct Deposit (default + complete) and Switch Direct Deposit
 * (complete) Content. Scope is the Content section only.
 */
const AccountHeroCard = ({
  backdrop,
  backgroundImage,
  bgcolor = 'primary.lighter',
  logo,
  name,
  status,
  subtitle,
  sx,
}: AccountHeroCardProps) => (
  <Box
    sx={{
      alignItems: 'center',
      bgcolor,
      ...(backgroundImage && { backgroundImage }),
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      pb: 16,
      position: 'relative',
      pt: LOGO_OVERHANG + 16,
      px: 16,
      width: '100%',
      ...sx,
    }}
  >
    {backdrop && (
      <Box aria-hidden={true} sx={{ inset: 0, position: 'absolute', zIndex: 0 }}>
        {backdrop}
      </Box>
    )}
    <Stack
      sx={{
        alignItems: 'center',
        // Theme-aware card surface (white in light, dark surface in dark) — not
        // `common.white`, which stays white and breaks dark mode.
        bgcolor: 'background.paper',
        borderRadius: '8px',
        // Figma: soft card drop-shadow (0 2px 8px rgba(0,0,0,0.12)).
        boxShadow: getLogoElevation,
        gap: 16,
        pb: 16,
        position: 'relative',
        pt: 36,
        px: 16,
        width: '100%',
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          left: '50%',
          position: 'absolute',
          top: -LOGO_OVERHANG,
          transform: 'translateX(-50%)',
        }}
      >
        {logo}
      </Box>
      <Stack sx={{ alignItems: 'center', gap: 12, minWidth: 0, width: '100%' }}>
        <Stack sx={{ alignItems: 'center', minWidth: 0, width: '100%' }}>
          {/* Figma name style: body2 metrics (13px / lineHeight 16) at weight
              600. MXUI `bold` renders 700, so set fontWeight explicitly. */}
          <Text sx={{ fontWeight: 600 }} truncate={true} variant="body2">
            {name}
          </Text>
          {subtitle && (
            <Text color="text.secondary" truncate={true} variant="caption">
              {subtitle}
            </Text>
          )}
        </Stack>
        {/* Center the status regardless of its own alignment (StatusPill
            defaults to `alignSelf: flex-start` for left-aligned contexts). */}
        {status && (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>{status}</Box>
        )}
      </Stack>
    </Stack>
  </Box>
)

export default AccountHeroCard
